/**
 * TanStack Query hooks for the webhook-delivery history view (Phase 9.11).
 *
 * Wraps:
 *   GET  /api/v1/admin/webhooks/deliveries                 -> useWebhookDeliveries
 *   POST /api/v1/admin/webhooks/deliveries/{id}/replay     -> useReplayDelivery
 *
 * The list endpoint accepts optional `endpoint_id` + `status` filters
 * and a `limit` (default 100, max 500 server-side).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  WebhookDeliveryResponse,
  WebhookDeliveryStatus,
} from "@/types/api";

export const WEBHOOK_DELIVERIES_QUERY_KEY = [
  "admin",
  "webhooks",
  "deliveries",
] as const;

export interface UseWebhookDeliveriesArgs {
  /** Filter to deliveries for a single endpoint. */
  endpointId?: string | null;
  /** Filter to a single delivery status (pending, success, ...). */
  status?: WebhookDeliveryStatus | null;
  /** Max rows to return. Defaults to 100. Server caps at 500. */
  limit?: number;
}

export function useWebhookDeliveries(
  args: UseWebhookDeliveriesArgs = {},
): UseQueryResult<WebhookDeliveryResponse[]> {
  const { endpointId = null, status = null, limit = 100 } = args;
  return useQuery({
    queryKey: [
      ...WEBHOOK_DELIVERIES_QUERY_KEY,
      { endpointId, status, limit },
    ],
    queryFn: async () => {
      const params: Record<string, string | number> = { limit };
      if (endpointId) params.endpoint_id = endpointId;
      if (status) params.status = status;
      const response = await api.get<WebhookDeliveryResponse[]>(
        "/admin/webhooks/deliveries",
        { params },
      );
      return response.data;
    },
  });
}

export function useReplayDelivery(): UseMutationResult<
  WebhookDeliveryResponse,
  Error,
  number
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (deliveryId: number) => {
      const response = await api.post<WebhookDeliveryResponse>(
        `/admin/webhooks/deliveries/${deliveryId}/replay`,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: WEBHOOK_DELIVERIES_QUERY_KEY,
      });
    },
  });
}
